import {Expressions} from '@packages/systems/wfdl';
import type {Expression} from '@packages/systems/wfdl-ast';
import {getDynamoBindingPaths} from './getDynamoBindingPaths';

export type ElementTopLevelPropBindingInfo = Record<
  string,
  TopLevelPropBindingInfo
>;

type TopLevelPropBindingInfo = {
  // The prop's value contains a Universal Binding -- any non-literal expression
  // (component/symbol override, page binding, CMS-via-UB, dyn-list select, ...).
  isUniversalBound: boolean;

  // The number of Dynamo (CMS) binding paths in the prop's value.
  dynamoUniversalBindingCount: number;
};

const orCombine =
  (a: boolean) =>
  (b: boolean): boolean =>
    a || b;

// True when the expression contains a Universal Binding anywhere in its own
// subtree. Mirrors `getDynamoBindingPaths`' traversal, but flags any binding
// (`Expressions.isBinding`), not just Dynamo-sourced ones, and stops at nested
// `Element` boundaries so a child element's bindings aren't attributed to the
// parent prop.
const hasUniversalBinding: (arg1: Expression) => boolean =
  Expressions.everything(orCombine)((expr) => {
    if (Expressions.isBinding(expr)) {
      return Expressions.stop(true);
    }
    if (Expressions.isElement(expr)) {
      return Expressions.stop(false);
    }
    return Expressions.resume(false);
  });

/**
 * Classifies each top-level prop of `elementData` by how its own value is bound.
 *
 * Props whose value is a plain literal are still included, with `isUniversalBound`
 * being `false` and dynamoUniversalBindingCount being 0.
 */
export function getTopLevelPropBindings(
  elementData: Expression
): ElementTopLevelPropBindingInfo {
  const fields = Expressions.getRecord(elementData);
  if (!fields) {
    return {};
  }

  const result: ElementTopLevelPropBindingInfo = {};
  for (const [prop, fieldExpression] of Object.entries(fields)) {
    const dynamoBindingsPaths = getDynamoBindingPaths(fieldExpression);

    result[prop] = {
      isUniversalBound: hasUniversalBinding(fieldExpression),
      dynamoUniversalBindingCount: dynamoBindingsPaths.length,
    };
  }
  return result;
}
